import { useCallback } from 'react';

export default function useSubmission({
  post,
  initialMenuTitle,
  formData,
  turnstileToken,
  hasTurnstileToken,
  attemptTurnstileVerification,
  resetTurnstile,
  hcaptchaToken,
  setTurnstileAttempted,
  resetAllCaptchas,
  soundSrc,
  setCurrentStep,
  setIsSubmitting,
  setShowNotification,
  setErrorMessage,
}) {
  const onSubmit = useCallback(async (data) => {
    setIsSubmitting(true);
    setShowNotification(false);
    setErrorMessage('');

    try {
      const selectedMenuName = data.menu || '';
      let finalMenu = initialMenuTitle;
      if (selectedMenuName) {
        finalMenu += finalMenu ? ` - ${selectedMenuName}` : selectedMenuName;
      }

      const submissionData = {
        ...data,
        date: window.selectedDate,
        guests: Number(data.guests),
        menu: finalMenu,
      };

      if (formData.giftcard) {
        submissionData.giftcard = formData.giftcard;
      }

      // Include waitlist selection flag if set in earlier step
      if (typeof formData.waitlist !== 'undefined') {
        submissionData.waitlist = !!formData.waitlist;
      }

      // If this is a waitlist submission, explicitly copy guests into guestsWaiting
      // (server already handles this, but sending it is harmless and explicit)
      const isWaitlist = !!submissionData.waitlist;
      if (isWaitlist && typeof submissionData.guestsWaiting === 'undefined') {
        submissionData.guestsWaiting = submissionData.guests;
      }

      let captchaToken = null;
      let captchaType = null;

      if (turnstileToken && hasTurnstileToken) {
        captchaToken = turnstileToken;
        captchaType = 'turnstile';
        console.log('Using existing Turnstile token for submission');
      }

      if (!captchaToken) {
        console.log('Attempting Turnstile verification at submit time...');
        const token = await attemptTurnstileVerification();
        setTurnstileAttempted(true);

        if (token) {
          captchaToken = token;
          captchaType = 'turnstile';
          console.log('Turnstile verification succeeded at submit time');
        } else {
          console.log('Turnstile failed at submit time, falling back to hCaptcha');
        }
      }

      if (!captchaToken && !hcaptchaToken) {
        console.log('No captcha token available, showing hCaptcha');
        setIsSubmitting(false);
        return { requireHCaptcha: true };
      }

      if (!captchaToken && hcaptchaToken) {
        captchaToken = hcaptchaToken;
        captchaType = 'hcaptcha';
        console.log('Using hCaptcha token for submission');
      }

      if (captchaType === 'turnstile') {
        submissionData['cf-turnstile-response'] = captchaToken;
        submissionData.verificationType = 'turnstile';
      } else if (captchaType === 'hcaptcha') {
        submissionData['h-captcha-response'] = captchaToken;
        submissionData.verificationType = 'hcaptcha';
      }

      console.log('Submitting reservation with data:', submissionData);

      // >>> CHANGE: use waitlist endpoint when applicable
      const url = `${window.baseDomain}api/reservations${isWaitlist ? '/waitlist' : ''}`;

      const response = await post(url, submissionData);
      console.log('Server response:', response);

      new Audio(soundSrc).play();

      try {
        localStorage.setItem('summary', JSON.stringify(submissionData));
      } catch (storageError) {
        console.error('Error saving summary to localStorage:', storageError);
      }

      setCurrentStep(3);
      return { success: true };
    } catch (error) {
      console.error('Submission failed:', error);
      if (error.response) {
        console.error('Error response data:', error.response.data);
        console.error('Error response status:', error.response.status);
      }

      const serverError = error.response?.data?.error;
      if (serverError === 'TURNSTILE_VERIFICATION_FAILED') {
        console.log('Server indicated TURNSTILE_VERIFICATION_FAILED, triggering hCaptcha fallback');
        setTurnstileAttempted(true);
        resetTurnstile();
        setIsSubmitting(false);
        return { requireHCaptcha: true };
      }

      resetAllCaptchas();

      if (
        error.response &&
        error.response.status === 400 &&
        error.response.data?.error === 'invalid_email'
      ) {
        setErrorMessage('Ongeldig e-mailadres. Gelieve een geldig e-mailadres in te voeren.');
      } else if (
        error.response &&
        error.response.status === 400 &&
        error.response.data?.error?.includes('already have a booking around this time')
      ) {
        setErrorMessage('U heeft al een reservering rond deze tijd en op deze dag. Bekijk uw e-mail of neem contact met ons op.');
      } else {
        setErrorMessage('De geselecteerde datum is niet meer geldig. Gelieve een nieuwe datum te kiezen.');
      }
      setIsSubmitting(false);
      setShowNotification(true);
      return { success: false };
    }
  }, [
    post,
    initialMenuTitle,
    formData,
    turnstileToken,
    hasTurnstileToken,
    attemptTurnstileVerification,
    resetTurnstile,
    hcaptchaToken,
    setTurnstileAttempted,
    resetAllCaptchas,
    soundSrc,
    setCurrentStep,
    setIsSubmitting,
    setShowNotification,
    setErrorMessage,
  ]);

  return onSubmit;
}
